<cm-inspector-section>
    <ng-container cm-title> Target Statistics</ng-container>
    <ng-container cm-content>
        <div>
            <div class="cm-mode-tabs">
                <cm-button
                    class="cm-mode"
                    [class.cm-active]="$targetStatsMode() === targetStatsModes.TextureSetRevision"
                    (click)="setTargetStatsMode(targetStatsModes.TextureSetRevision)"
                    [disabled]="$disabledEdits()"
                >
                    <ng-container cm-left-icon><i class="far fa-layer-group"></i></ng-container>
                    <ng-container cm-text>Texture Set</ng-container>
                </cm-button>
                <cm-button
                    class="cm-mode"
                    [class.cm-active]="$targetStatsMode() === targetStatsModes.ReferenceImage"
                    (click)="setTargetStatsMode(targetStatsModes.ReferenceImage)"
                    [disabled]="$disabledEdits()"
                >
                    <ng-container cm-left-icon><i class="far fa-image"></i></ng-container>
                    <ng-container cm-text>Upload</ng-container>
                </cm-button>
                <cm-button
                    class="cm-mode"
                    [class.cm-active]="$targetStatsMode() === targetStatsModes.ColorPicker"
                    (click)="setTargetStatsMode(targetStatsModes.ColorPicker)"
                    [disabled]="$disabledEdits()"
                >
                    <ng-container cm-left-icon><i class="far fa-palette"></i></ng-container>
                    <ng-container cm-text>Color Picker</ng-container>
                </cm-button>
            </div>
            <div class="cm-mode-content">
                @if ($computingTargetStats()) {
                    <div class="cm-reference-image-analyzing">
                        <cm-loading-spinner-icon></cm-loading-spinner-icon>
                        <span class="cm-label">Computing target color statistics…</span>
                    </div>
                } @else if ($targetStatsMode() === targetStatsModes.TextureSetRevision) {
                    <cm-hint>
                        <ng-container cm-text>Choose an existing texture to compute target color statistics from it.</ng-container>
                    </cm-hint>
                    @if ($referenceUnavailable()) {
                        <div class="cm-reference-name cm-label">The previously chosen texture is no longer available.</div>
                    }
                    @if ($tabReferenceImage(); as referenceImage) {
                        <div class="cm-reference-name" [title]="referenceImage.displayName">{{ referenceImage.displayName }}</div>
                    }
                    <cm-button class="cm-choose-reference" (click)="chooseReferenceTextureMap()" [disabled]="$referenceControlsDisabled()">
                        <ng-container cm-left-icon><i class="far fa-layer-group"></i></ng-container>
                        <ng-container cm-text>Choose texture</ng-container>
                    </cm-button>
                } @else if ($targetStatsMode() === targetStatsModes.ReferenceImage) {
                    <cm-hint>
                        <ng-container cm-text>Drop a reference image here to compute target color statistics from it.</ng-container>
                    </cm-hint>
                    @if ($tabReferenceImage(); as referenceImage) {
                        <div class="cm-reference-name" [title]="referenceImage.displayName">{{ referenceImage.displayName }}</div>
                    }
                    <cm-drop-files
                        class="cm-reference-image-drop"
                        [class.cm-disabled]="$disabledEdits()"
                        [mimeTypeFilter]="MimeType.Images"
                        [showDropzone]="true"
                        [enableDrop]="!$disabledEdits()"
                        [maxFiles]="1"
                        subtitle="or click to browse"
                        (filesDropped)="onReferenceImageDropped($event)"
                    ></cm-drop-files>
                } @else {
                    <cm-hint>
                        <ng-container cm-text>Pick a target color to compute target color statistics from it.</ng-container>
                    </cm-hint>
                    <div class="cm-color-picker-wrapper" [class.cm-disabled]="$disabledEdits()">
                        <cm-color-input (valueChange)="onColorPicked($event)"></cm-color-input>
                    </div>
                }
                @if ($tabReferenceImage(); as referenceImage) {
                    <div class="cm-reference-region">
                        @if (referenceImage.previewUrl; as previewUrl) {
                            <cm-reference-region-selector
                                [previewUrl]="previewUrl"
                                [imageSize]="referenceImage"
                                [region]="$referenceRegion()"
                                [disabled]="$referenceControlsDisabled()"
                                (regionChange)="onReferenceRegionChange($event)"
                            ></cm-reference-region-selector>
                            <div class="cm-reference-region-info">
                                <span class="cm-label">{{ $referenceRegionLabel() }}</span>
                                @if ($referenceRegion()) {
                                    <cm-button (click)="onReferenceRegionChange(undefined)" [disabled]="$referenceControlsDisabled()">
                                        <ng-container cm-text>Use entire image</ng-container>
                                    </cm-button>
                                } @else {
                                    <span class="cm-label">Drag on the image to use only a part of it</span>
                                }
                            </div>
                        } @else {
                            <span class="cm-label">There is no preview to select a region on, so the entire image is used.</span>
                        }
                    </div>
                }
            </div>
        </div>
        <table class="cm-stats-table">
            <thead>
                <tr>
                    <td></td>
                    <th scope="col">Mean</th>
                    <th scope="col">Std. Dev.</th>
                </tr>
            </thead>
            <tbody>
                @for (row of $targetStatsRows(); track row.label) {
                    <tr>
                        <th scope="row">{{ row.label }}</th>
                        <td>
                            @if ($isDebugEnabled()) {
                                <cm-input-container>
                                    <ng-container cm-input>
                                        <cm-numeric-input
                                            [value]="row.mean"
                                            (valueChange)="row.setMean($event)"
                                            [decimalPlaces]="3"
                                            [disabled]="$disabledEdits()"
                                        ></cm-numeric-input>
                                    </ng-container>
                                </cm-input-container>
                            } @else {
                                {{ row.mean | number: "1.3-3" }}
                            }
                        </td>
                        <td>
                            @if ($isDebugEnabled()) {
                                <cm-input-container>
                                    <ng-container cm-input>
                                        <cm-numeric-input
                                            [value]="row.stddev"
                                            (valueChange)="row.setStddev($event)"
                                            [decimalPlaces]="3"
                                            [disabled]="$disabledEdits()"
                                        ></cm-numeric-input>
                                    </ng-container>
                                </cm-input-container>
                            } @else {
                                {{ row.stddev | number: "1.3-3" }}
                            }
                        </td>
                    </tr>
                }
            </tbody>
        </table>
    </ng-container>
</cm-inspector-section>
<cm-inspector-section>
    <ng-container cm-title> Adjustments</ng-container>
    <ng-container cm-content>
        <div>
            <span class="cm-label">Luminance</span>
            <cm-slider [min]="0" [max]="1" [value]="$luminance()" (valueChange)="setLuminance($event)" [disabled]="$disabledEdits()"></cm-slider>
        </div>
        <div>
            <span class="cm-label">Color Intensity</span>
            <cm-slider [min]="0" [max]="1" [value]="$colorIntensity()" (valueChange)="setColorIntensity($event)" [disabled]="$disabledEdits()"></cm-slider>
        </div>
        <div>
            <span class="cm-label">Fade</span>
            <cm-slider [min]="0" [max]="1" [value]="$fade()" (valueChange)="setFade($event)" [disabled]="$disabledEdits()"></cm-slider>
        </div>
    </ng-container>
</cm-inspector-section>
